import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase.ts';
import './ChristmasDayQuiz.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';
const QUESTION_TIME = 20; // 20 seconds per question

interface Question {
  id: number;
  question_text: string;
  correct_answer: string;
  incorrect_answer_1: string;
  incorrect_answer_2: string;
  incorrect_answer_3: string;
  difficulty: number;
  category?: string;
  category_name?: string;
  category_emoji?: string;
  image_url?: string;
}

interface ShuffledAnswer {
  text: string;
  isCorrect: boolean;
  originalIndex: number;
}

const ChristmasDayQuiz: React.FC = () => {
  const { day } = useParams<{ day: string }>();
  const navigate = useNavigate();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  
  const [user, setUser] = useState<any>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [timer, setTimer] = useState(QUESTION_TIME);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gameEnded, setGameEnded] = useState(false);
  const [endReason, setEndReason] = useState<'wrong' | 'timeout' | 'victory' | 'already_played' | null>(null);
  const [correctAnswer, setCorrectAnswer] = useState<string>('');
  const [alreadyPlayed, setAlreadyPlayed] = useState(false);
  const [todayScore, setTodayScore] = useState<number | null>(null);
  const [showAnswerFeedback, setShowAnswerFeedback] = useState(false);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState<boolean | null>(null);
  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState<number | null>(null);
  const [shuffledAnswers, setShuffledAnswers] = useState<ShuffledAnswer[]>([]);
  const [themeInfo, setThemeInfo] = useState<{ theme: string; emoji: string } | null>(null);

  // Auth listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
      if (!u) navigate('/login');
    });
    return () => unsub();
  }, [navigate]);

  // Shuffle answers for current question
  const shuffleAnswers = useCallback((question: Question): ShuffledAnswer[] => {
    const answers: ShuffledAnswer[] = [
      { text: question.correct_answer, isCorrect: true, originalIndex: 0 },
      { text: question.incorrect_answer_1, isCorrect: false, originalIndex: 1 },
      { text: question.incorrect_answer_2, isCorrect: false, originalIndex: 2 },
      { text: question.incorrect_answer_3, isCorrect: false, originalIndex: 3 }
    ];
    // Fisher-Yates shuffle
    for (let i = answers.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [answers[i], answers[j]] = [answers[j], answers[i]];
    }
    return answers;
  }, []);

  // Fetch quiz questions
  useEffect(() => {
    async function fetchQuiz() {
      if (!user || !day) return;

      try {
        setLoading(true);
        setError(null);

        const token = await user.getIdToken();
        const response = await fetch(`${API_URL}/api/christmas/quiz/${day}?maxQuestions=24`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const data = await response.json();
          if (data.alreadyCompleted) {
            setAlreadyPlayed(true);
            setTodayScore(data.score || 0);
            setGameEnded(true);
            setEndReason('already_played');
            setLoading(false);
            return;
          }
          throw new Error(data.error || 'Failed to fetch quiz');
        }

        const data = await response.json();
        
        if (data.questions && data.questions.length > 0) {
          setQuestions(data.questions);
          setThemeInfo({ theme: data.theme, emoji: data.emoji });
          setShuffledAnswers(shuffleAnswers(data.questions[0]));
          setCurrentIndex(0);
          setScore(0);
          setTimer(QUESTION_TIME);
          setRunning(true);
          startTimeRef.current = Date.now();
        } else {
          throw new Error('Aucune question disponible');
        }
      } catch (err: any) {
        setError(err.message || 'Error loading quiz');
      } finally {
        setLoading(false);
      }
    }
    
    if (!authLoading && user) {
      fetchQuiz();
    }
  }, [user, day, authLoading, shuffleAnswers]);

  // Timer effect
  useEffect(() => {
    if (!running || showAnswerFeedback) {
      return;
    }

    timerRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          handleGameOver('timeout');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [running, showAnswerFeedback, currentIndex]);

  // Save progress to server
  const saveProgress = useCallback(async (finalScore: number, completed: boolean) => {
    if (!user || !day) return;

    try {
      // Calculer le temps en secondes, avec un minimum de 1 seconde
      let timeSec = startTimeRef.current > 0 ? Math.floor((Date.now() - startTimeRef.current) / 1000) : 0;
      // Garantir un minimum de 1 seconde si le jeu a vraiment été joué
      if (timeSec === 0 && startTimeRef.current > 0) {
        timeSec = 1;
      }
      const token = await user.getIdToken();
      await fetch(`${API_URL}/api/christmas/complete/${day}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ score: finalScore, timeSec, completed })
      });
    } catch (err) {
      console.error('Failed to save progress:', err);
    }
  }, [user, day]);

  // Handle game over
  const handleGameOver = useCallback((reason: 'wrong' | 'timeout' | 'victory') => {
    setRunning(false);
    setGameEnded(true);
    setEndReason(reason);
    
    if (timerRef.current) clearInterval(timerRef.current);
    
    if (reason === 'wrong' && questions[currentIndex]) {
      setCorrectAnswer(questions[currentIndex].correct_answer);
    }
    
    // Always mark as completed (player finished the day, regardless of outcome)
    saveProgress(score, true);
  }, [questions, currentIndex, score, saveProgress]);

  // Handle answer selection
  const handleAnswer = (answerIndex: number) => {
    if (!questions[currentIndex] || showAnswerFeedback) return;
    
    const answer = shuffledAnswers[answerIndex];
    setSelectedAnswerIndex(answerIndex);
    setRunning(false);
    if (timerRef.current) clearInterval(timerRef.current);
    
    const isCorrect = answer.isCorrect;
    setLastAnswerCorrect(isCorrect);
    setShowAnswerFeedback(true);
    
    // Delay before continuing (3 seconds for correct, 2 for wrong)
    const delay = isCorrect ? 3000 : 2000;
    
    setTimeout(() => {
      setShowAnswerFeedback(false);
      setSelectedAnswerIndex(null);
      setLastAnswerCorrect(null);
      
      if (!isCorrect) {
        setCorrectAnswer(questions[currentIndex].correct_answer);
        handleGameOver('wrong');
      } else {
        const newScore = score + 1;
        setScore(newScore);
        
        // Check if this was the last question
        if (currentIndex >= questions.length - 1) {
          setGameEnded(true);
          setEndReason('victory');
          setRunning(false);
          saveProgress(newScore, true);
        } else {
          // Move to next question
          const nextIndex = currentIndex + 1;
          setCurrentIndex(nextIndex);
          // Réinitialiser explicitement pour iOS
          setShuffledAnswers([]);
          setTimeout(() => {
            setShuffledAnswers(shuffleAnswers(questions[nextIndex]));
            setTimer(QUESTION_TIME);
            setRunning(true);
          }, 0);
        }
      }
    }, delay);
  };

  // Format time display
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get total time elapsed
  const getTotalTime = (): number => {
    return Math.floor((Date.now() - startTimeRef.current) / 1000);
  };

  if (authLoading || loading) {
    return (
      <div className="xmas-quiz-container">
        <div className="xmas-quiz-loading">
          <div className="loading-icon">🎄</div>
          <p>Chargement du quiz...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="xmas-quiz-container">
        <div className="xmas-quiz-error">
          <div className="error-icon">❌</div>
          <h2>Erreur</h2>
          <p>{error}</p>
          <button className="xmas-btn" onClick={() => navigate('/christmas')}>
            Retour au calendrier
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];

  return (
    <div className="xmas-quiz-container">
      {/* Header */}
      <div className="xmas-quiz-header">
        <h2 className="xmas-quiz-title">
          <span className="title-emoji">{themeInfo?.emoji || '🎄'}</span>
          Jour {day}
        </h2>
        <div className="xmas-quiz-nav">
          <Link to="/christmas/leaderboard" className="xmas-quiz-nav-link">
            📋 Classement
          </Link>
          <button onClick={() => navigate('/christmas')} className="xmas-quiz-back-btn">
            ← Retour
          </button>
        </div>
      </div>

      <div className="xmas-quiz-description">
        <p>
          🎯 <strong>{themeInfo?.theme || 'Quiz de Noël'}</strong> • 
          ⏰ 20 secondes par question • 
          ❌ Une erreur met fin au quiz
        </p>
      </div>

      <div className="xmas-quiz-content">
        <div className="xmas-quiz-game">
          {!alreadyPlayed && !gameEnded && (
            <>
              {/* Stats */}
              <div className="xmas-quiz-stats">
                <div className="xmas-quiz-stat">
                  <span className="xmas-quiz-stat-value">{score}</span>
                  <span className="xmas-quiz-stat-label">Score</span>
                </div>
                <div className="xmas-quiz-stat">
                  <span className="xmas-quiz-stat-value">#{currentIndex + 1}</span>
                  <span className="xmas-quiz-stat-label">Question</span>
                </div>
              </div>

              {/* Timer Progress */}
              {running && (
                <div className="timer-progress-container">
                  <div className="timer-progress-header">
                    <span className="timer-progress-label">Temps restant</span>
                    <span className={`timer-progress-value ${timer <= 5 ? 'warning' : ''}`}>
                      ⏱️ {timer}s
                    </span>
                  </div>
                  <div className="timer-progress-bar">
                    <div 
                      className={`timer-progress-fill ${timer <= 5 ? 'warning' : ''}`}
                      style={{ width: `${(timer / QUESTION_TIME) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Question Card */}
          {currentQuestion && !gameEnded ? (
            <div className="xmas-quiz-question-card">
              <div className="xmas-quiz-question-meta">
                <span className="xmas-quiz-question-category">
                  {currentQuestion.category_emoji || '🎄'} {currentQuestion.category_name || themeInfo?.theme || 'Quiz'}
                </span>
                <div className="xmas-quiz-question-difficulty">
                  {Array.from({ length: currentQuestion.difficulty || 1 }, (_, i) => (
                    <span key={i} className="difficulty-star">★</span>
                  ))}
                </div>
              </div>

              {currentQuestion.image_url && (
                <div className="xmas-quiz-question-image">
                  <img 
                    src={currentQuestion.image_url} 
                    alt="Question" 
                    className="question-image"
                  />
                </div>
              )}

              <div className="xmas-quiz-question-text">
                {currentQuestion.question_text}
              </div>

              <div className="xmas-quiz-options">
                {shuffledAnswers.map((answer, i) => {
                  let buttonClass = "xmas-quiz-option";
                  
                  if (showAnswerFeedback) {
                    if (answer.isCorrect) {
                      buttonClass += " correct-answer";
                    } else if (i === selectedAnswerIndex) {
                      buttonClass += " wrong-answer";
                    }
                  }
                  
                  return (
                    <button
                      key={`${questions[currentIndex]?.id}-${currentIndex}-${i}-${answer.text.substring(0, 10)}`}
                      onClick={() => handleAnswer(i)}
                      className={buttonClass}
                      disabled={!running || showAnswerFeedback}
                    >
                      <span className="option-letter">{String.fromCharCode(65 + i)}</span>
                      {answer.text}
                      {showAnswerFeedback && answer.isCorrect && (
                        <span className="feedback-icon">✓</span>
                      )}
                      {showAnswerFeedback && i === selectedAnswerIndex && !answer.isCorrect && (
                        <span className="feedback-icon">✗</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Game Over Screen */}
          {gameEnded && (
            <div className="xmas-quiz-game-over">
              <div className="game-over-content">
                <div className="game-over-icon">
                  {endReason === 'wrong' && '💔'}
                  {endReason === 'timeout' && '⏰'}
                  {endReason === 'victory' && '🎉'}
                  {endReason === 'already_played' && '✅'}
                </div>
                
                <h3 className="game-over-title">
                  {endReason === 'wrong' && 'Mauvaise réponse !'}
                  {endReason === 'timeout' && 'Temps écoulé !'}
                  {endReason === 'victory' && `🎄 Jour ${day} complété !`}
                  {endReason === 'already_played' && 'Déjà joué !'}
                </h3>
                
                <div className="game-over-stats">
                  <div className="final-score">
                    <span className="score-label">
                      {endReason === 'already_played' ? 'Votre score' : 'Score final'}
                    </span>
                    <span className="score-value">
                      {endReason === 'already_played' ? (todayScore || 0) : score}
                    </span>
                    <span className="score-questions">
                      / {questions.length} questions
                    </span>
                  </div>

                  {endReason === 'victory' && (
                    <div className="time-display">
                      <span className="time-label">Temps total</span>
                      <span className="time-value">⏱️ {formatTime(getTotalTime())}</span>
                    </div>
                  )}
                  
                  {(endReason === 'wrong' || endReason === 'timeout') && correctAnswer && (
                    <div className="correct-answer-reveal">
                      <span className="correct-label">La bonne réponse était :</span>
                      <span className="correct-value">✅ {correctAnswer}</span>
                    </div>
                  )}
                </div>

                {endReason === 'victory' && (
                  <div className="victory-message">
                    <p>🎅 Bravo ! Tu as répondu correctement à toutes les questions !</p>
                  </div>
                )}

                {(endReason === 'wrong' || endReason === 'timeout') && (
                  <div className="defeat-message">
                    <p>Une mauvaise réponse met fin au quiz. Reviens demain pour le jour suivant !</p>
                  </div>
                )}

                <div className="game-over-actions">
                  <button onClick={() => navigate('/christmas')} className="xmas-btn primary">
                    📅 Retour au calendrier
                  </button>
                  <button onClick={() => navigate('/christmas/leaderboard')} className="xmas-btn secondary">
                    🏆 Voir le classement
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChristmasDayQuiz;
